/** Schemes consumers may explicitly request through the show-diff API. */
export type PublicColorScheme = 'standard' | 'traditional';

/** Attribution-only participant slots selected by the plugin, never by consumers. */
export const PARTICIPANT_COLOR_SCHEMES = [
	'red-bolder',
	'blue-bolder',
	'green-bolder',
	'yellow-bolder',
	'purple-bolder',
	'magenta-bolder',
	'teal-bolder',
	'orange-bolder',
	'lime-bolder',
	'gray-bolder',
	'blue-subtle',
	'red-subtle',
	'orange-subtle',
	'yellow-subtle',
	'green-subtle',
	'teal-subtle',
	'purple-subtle',
	'magenta-subtle',
] as const;

export type DiffParticipantColorScheme = (typeof PARTICIPANT_COLOR_SCHEMES)[number];

/** ADS accent hues used by the public and participant colour schemes. */
export type AdsAccentColor =
	| 'blue'
	| 'gray'
	| 'green'
	| 'lime'
	| 'magenta'
	| 'orange'
	| 'purple'
	| 'red'
	| 'teal'
	| 'yellow';

/** A public scheme or an attribution participant slot resolved internally for one change. */
export type ColorScheme = PublicColorScheme | DiffParticipantColorScheme;

/** A colour value that the factory expands into a complete set of diff-safe ADS tokens. */
export type DiffSchemeColor = AdsAccentColor | DiffParticipantColorScheme;

/**
 * A show-diff colour scheme. Colour fields are ADS hues or shared participant slots that
 * `factory.ts` expands into token paths; the rest encode structural differences no colour can
 * express. Ring widths, padding, radius and pointer-events are fixed in the factory, not per scheme.
 */
export type DiffColorScheme = {
	/** Stacking order of the square added-cell overlay. The rounded variant is always 2. */
	addedCellOverlayZIndex: 1 | 2;
	/** Deleted inline content, active/focused state. */
	deleteActiveColor: DiffSchemeColor;
	/** Deleted inline content. */
	deleteColor: DiffSchemeColor;
	/** Active deleted-block ring: pale `background.accent.*.subtler.pressed` vs saturated `border.accent.*`. */
	deletedBlockOutlineActiveEmphasis: 'background' | 'border';
	/** Deleted cell outline: `color.border.disabled` hairline vs opaque accent border. */
	deletedCellBorderProminence: 'subtle' | 'accent';
	/** Deleted cell overlays. Gray in both current schemes. */
	deletedCellColor: DiffSchemeColor;
	/** Opacity of the deleted cell overlay background. Counterpart to `insertedCellOpacity`. */
	deletedCellOpacity: number;
	/** Deleted inline content: tint the strikethrough, or tint and dim the glyph itself. */
	deletedInlineTreatment: 'strikethrough' | 'glyphTint';
	/** Resting "REMOVED" lozenge tint. Gray in both current schemes; the active state uses `deleteActiveColor`. */
	deletedLozengeColor: DiffSchemeColor;
	/**
	 * "REMOVED" lozenge label, in both states: a fixed inverse tone that reads on any tint, or the
	 * accent text colour matching `deletedLozengeColor`.
	 */
	deletedLozengeTextTone: 'inverse' | 'accent';
	/**
	 * Resting ring colour for a deleted media/embed card under the a11y-fixes treatment. Its own role
	 * rather than a derivation of `deleteColor`: that treatment rings red even for gray deletes.
	 */
	deletedMediaRingColor: DiffSchemeColor;
	/** Deleted block ring: 4px stateful ring plus marker variables, or one resting treatment. */
	deletedNodeEmphasis: 'stateful' | 'static';
	/** Active deleted-blockquote ring tone, for the nodeview-wrapped case only. */
	deletedQuoteNodeActiveTone: 'backgroundPressed' | 'borderAccent';
	/** Deleted blockquote default state: 1px ring with padding, or a 2px left border. Active is always a ring. */
	deletedQuoteNodeShape: 'ring' | 'borderLeft';
	/** Deleted row de-emphasis: tint the line-through, or tint the row text. Both read `deleteColor`. */
	deletedRowTreatment: 'strikeColor' | 'textTint';
	/** Inserted content, active/focused state. */
	insertActiveColor: DiffSchemeColor;
	/** Inserted content, default state. */
	insertColor: DiffSchemeColor;
	/** Opacity of the inserted cell overlay background. */
	insertedCellOpacity: number;
	/** Inserted content under the extended experience: underline, or a border-bottom rule with padding. */
	insertedInlineTreatment: 'underline' | 'borderBottom';
	/** Inserted block ring: 4px stateful ring, or one static 1px accent ring. */
	insertedNodeEmphasis: 'stateful' | 'static';
	/** `text-decoration-style` for the inserted-text underline. */
	insertUnderlineStyle: 'solid' | 'dotted';
	/** Whether the rounded added-cell overlay inherits the cell's `border`. Deleted always does. */
	roundedAddedCellOverlayInheritsBorder: boolean;
	/** Whether a deleted embedCard gets a strike at all. Standard draws none. */
	strikesDeletedEmbedCard: boolean;
};
